import useChartSort from '@/composables/chart-sort';
import useCustomBuckets from '@/composables/grid-page/chart/custom-buckets';
import useGoalCycles from '@/composables/goal-cycle/goal-cycle';
import useGoalProperty from '@/composables/property/goal-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePropertyOptions from '@/composables/property-option/property-option';
import useSpaces from '@/composables/space/spaces';
import useStatusProperty from '@/composables/goal/status-property';
import useUsers from '@/composables/user/users';
import { aggregatorType, chartGroupByOption, gridPageChartType, propertyType } from 'shared/constants.json';
import { cleanResult, fillMissingCategories, getDayFromISOString, getDisplayValue, getMonthFromISOString, getQuarterFromISOString, getWeekFromISOString, getYearFromISOString, isDateGrouping, roundNumber } from '@/composables/grid-page/chart/utils';
import { computed } from 'vue';
import { copy } from 'shared/lib/copy';
import { getColor } from '@/lib/charts/colors';
import { goal as goalConfig } from 'shared/api/query/configs.json';

export default function useChartGrouping(chart, resultRaw) {
  const customBucket = computed(() => chart.value.groupBy.customBucket);
  const { findBucketLabel: findCustomBucketLabel } = useCustomBuckets(customBucket, resultRaw, computed(() => chart.value.groupBy));
  const stackedCustomBucket = computed(() => chart.value.aggregationConfig.stackOptions.customBucket);
  const { findBucketLabel: findStackedBucketLabel } = useCustomBuckets(stackedCustomBucket, resultRaw, computed(() => chart.value.aggregationConfig.stackOptions));
  const { userLang } = useLoggedInUser();
  const { selectSingle: selectGoalCycle, goalCycles } = useGoalCycles();
  const { selectSingle: selectPropertyOption } = usePropertyOptions();
  const { selectSingle: selectSpace, spaces } = useSpaces();
  const { selectSingle: selectUser, users } = useUsers();
  const { statusProperty } = useGoalProperty();
  const { leafOptions: leafStatuses } = useStatusProperty(statusProperty, userLang);

  const { sort } = useChartSort();

  const isStacked = computed(() => [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column, gridPageChartType.table].includes(chart.value.chartType)
      && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const dateGroupingFunctions = {
    [chartGroupByOption.day]: getDayFromISOString,
    [chartGroupByOption.week]: getWeekFromISOString,
    [chartGroupByOption.month]: getMonthFromISOString,
    [chartGroupByOption.quarter]: getQuarterFromISOString,
    [chartGroupByOption.year]: getYearFromISOString,
  };

  const aggregationFn = {
    [aggregatorType.count]: (acc, item) => ({ count: acc.count + item.count }),
    [aggregatorType.sum]: (acc, item) => ({ sum: acc.sum + item.sum }),
    [aggregatorType.avg]: (acc, item) => {
      const total = (acc.avg * acc.count) + (item.avg * item.count);
      const count = acc.count + item.count;
      return { avg: total / count, count };
    },
    [aggregatorType.min]: (acc, item) => {
      if (item.min < acc.min) {
        return { min: item.min };
      }
      return acc;
    },
    [aggregatorType.max]: (acc, item) => {
      if (item.max > acc.max) {
        return { max: item.max };
      }
      return acc;
    },
    [aggregatorType.countDistinct]: (acc, item) => {
      if (acc.values[item.subGroup] === undefined) {
        acc[aggregatorType.countDistinct] += 1;
        acc.values[item.subGroup] = {};
      }
      return acc;
    },
  };

  const totalAggregator = {
    [aggregatorType.count]: aggregationFn[aggregatorType.count],
    [aggregatorType.sum]: aggregationFn[aggregatorType.sum],
    [aggregatorType.avg]: aggregationFn[aggregatorType.avg],
    [aggregatorType.min]: aggregationFn[aggregatorType.min],
    [aggregatorType.max]: aggregationFn[aggregatorType.max],
    [aggregatorType.countDistinct]: (acc, item) => {
      acc.values = { ...acc.values, ...item.values };
      acc[aggregatorType.countDistinct] = Object.keys(acc.values).length;
      return acc;
    },
  };

  const initialValue = {
    [aggregatorType.count]: { count: 0 },
    [aggregatorType.sum]: { sum: 0 },
    [aggregatorType.avg]: { avg: 0, count: 0 },
    [aggregatorType.min]: { min: Infinity },
    [aggregatorType.max]: { max: -Infinity },
    [aggregatorType.countDistinct]: { [aggregatorType.countDistinct]: 0, values: {} },
  };

  const getGroupEntity = (groupBy, groupValue) => {
    if (groupBy.groupBy === chartGroupByOption.none) {
      return undefined;
    }

    if (groupBy.isProperty === false && groupBy.edge === goalConfig.edges.goalCycle) {
      return selectGoalCycle(groupValue);
    }

    if (groupBy.isProperty === false) {
      return undefined;
    }

    if (groupBy.property?.type === propertyType.space) {
      return selectSpace(groupValue);
    }

    if (groupBy.property?.type === propertyType.user) {
      return selectUser(groupValue);
    }

    if (groupBy.property?.options) {
      return selectPropertyOption(groupValue);
    }

    return undefined;
  };

  const getGroups = (result, groupBy, valueKey, isStackOptions = false) => {
    const groupMap = new Map();
    result.forEach((item) => {
      const group = groupByMap(groupBy.groupBy, isStackOptions)(item[valueKey], userLang.value);
      if (!groupMap.has(group)) {
        groupMap.set(group, true);
      }
    });

    return Array.from(groupMap.keys()).map((g) => {
      const entity = getGroupEntity(groupBy, g);
      return {
        title: getDisplayValue(g, entity, groupBy, userLang.value),
        originalValue: g,
        color: getColor(entity?.color),
      };
    });
  };

  const groupByMap = (groupBy, isStackOptions = false) => {
    if (isDateGrouping(groupBy)) {
      return dateGroupingFunctions[groupBy];
    }
    if (groupBy === chartGroupByOption.bucket) {
      if (isStackOptions) {
        return findStackedBucketLabel;
      }
      return findCustomBucketLabel;
    }
    return (val) => val;
  };

  const groupResult = (result, aggregation, group, subGroup = null) => result.reduce((acc, item) => {
    const groupKey = groupByMap(group.groupBy)(item[group.valueKey], userLang.value);
    if (!acc[groupKey]) {
      acc[groupKey] = { groups: {}, ...copy(initialValue[aggregation]) };
    }
    acc[groupKey] = { ...acc[groupKey], ...aggregationFn[aggregation](acc[groupKey], item) };
    if (subGroup === null) {
      return acc;
    }
    const subGroupKey = groupByMap(subGroup.groupBy, true)(item[subGroup.valueKey], userLang.value);
    if (!acc[groupKey].groups[subGroupKey]) {
      acc[groupKey].groups[subGroupKey] = copy(initialValue[aggregation]);
    }
    acc[groupKey].groups[subGroupKey] = { ...acc[groupKey].groups[subGroupKey], ...aggregationFn[aggregation](acc[groupKey].groups[subGroupKey], item) };
    return acc;
  }, {});

  const processChartData = (result) => {
    const aggregator = chart.value.aggregationConfig.aggregation.aggregator;
    const excludeNullValues = chart.value.excludeNullValues ?? false;
    const fillContext = computed(() => ({
      goalCycles: goalCycles.value,
      spaces: spaces.value,
      users: users.value,
      leafStatuses: leafStatuses.value,
      getColor,
    }));

    const filteredResult = cleanResult(
      result,
      chart.value.groupBy,
      chart.value.aggregationConfig.stackOptions,
      userLang.value,
      fillContext.value,
    );

    if (isStacked.value) {
      const stackedGroups = getGroups(filteredResult, chart.value.aggregationConfig.stackOptions, 'stackGroup', true);
      const groups = getGroups(filteredResult, chart.value.groupBy, 'val');
      const groupedRes = groupResult(
        filteredResult,
        aggregator,
        { groupBy: chart.value.groupBy.groupBy, valueKey: 'val' },
        { groupBy: chart.value.aggregationConfig.stackOptions.groupBy, valueKey: 'stackGroup' },
      );
      const aggregatedTotal = Object.values(groupedRes).reduce(
        totalAggregator[aggregator],
        copy(initialValue[aggregator]),
      );
      const sortedGroups = sort(groups, chart.value.groupBy);
      const categories = fillMissingCategories(sortedGroups, chart.value.groupBy, userLang.value, excludeNullValues, fillContext.value);
      const sortedStackedGroups = sort(stackedGroups, chart.value.aggregationConfig.stackOptions);
      const filledStackedGroups = fillMissingCategories(sortedStackedGroups, chart.value.aggregationConfig.stackOptions, userLang.value, excludeNullValues, fillContext.value);
      return {
        categories: categories.map((category) => category.title),
        series: filledStackedGroups.map((stackGroup) => ({
          name: stackGroup.title,
          color: stackGroup.color,
          data: categories.map((category) => {
            if (groupedRes[category.originalValue] === undefined || groupedRes[category.originalValue].groups[stackGroup.originalValue] === undefined) {
              return { y: 0, name: stackGroup.title };
            }
            return { y: roundNumber(groupedRes[category.originalValue].groups[stackGroup.originalValue][aggregator]), name: stackGroup.title };
          }),
        })),
        total: roundNumber(aggregatedTotal[aggregator]),
      };
    }
    const groupedResult = groupResult(
      filteredResult,
      aggregator,
      { groupBy: chart.value.groupBy.groupBy, valueKey: 'val' },
    );
    const aggregatedTotal = Object.values(groupedResult).reduce(
      totalAggregator[aggregator],
      copy(initialValue[aggregator]),
    );
    const total = roundNumber(aggregatedTotal[aggregator]);

    const groups = getGroups(filteredResult, chart.value.groupBy, 'val');
    const sortedGroups = sort(groups, chart.value.groupBy);
    const categories = fillMissingCategories(sortedGroups, chart.value.groupBy, userLang.value, excludeNullValues, fillContext.value);

    return {
      categories: categories.map((category) => category.title),
      series: [{
        data: categories.map((category) => ({
          name: category.title,
          y: groupedResult[category.originalValue] ? roundNumber(groupedResult[category.originalValue][aggregator]) : 0,
          color: category.color,
        })),
      }],
      total,
    };
  };

  return { processChartData };
}
